import { resolveHoverColor } from "./luminance";

export interface PortalLinkBranding {
  linkColor?: string | null;
}

// Link-style elements that pick up the merchant link color anywhere in the
// portal:
//   - anchor tags from MaybeDisplayHtml and RedoExternalLink
//   - RedoButton hierarchy="link" and hierarchy="link_underline" (rendered
//     as <button data-hierarchy="..." data-theme="..."> — see
//     `linkButtonSpecificSelectors` below for the high-specificity form
//     used to beat the design system's default link color rule).
// The hover variant is auto-derived by darkening light colors / lightening
// dark ones via resolveHoverColor.

export function buildPortalLinkCssRules(
  s: string,
  root: string,
  branding: PortalLinkBranding | null | undefined,
): string[] {
  const raw = branding?.linkColor;
  const color = typeof raw === "string" && raw.length > 0 ? raw : undefined;
  if (color === undefined) {
    return [];
  }

  // The themed scope: the customer-portal root AND modal surfaces that
  // render via React portals at <body> — outside the customer-portal DOM
  // subtree. Mirrors the same approach used by buildPortalBackgroundCssRules
  // so links inside modals still pick up the merchant link color.
  const scopes = `${root}, [data-ui="portal-modal"]`;
  // Combine both scopes with each inner selector for descendant matching.
  const descendantPrefixes = [s, '[data-ui="portal-modal"] '];

  // Use the most specific button-hierarchy variants so we beat the design
  // system's `.theme-normal.hierarchy-link-underline { color: ... }` rule
  // (which sets the link button color via the secondary-button fg token).
  // Equivalent in matching but higher specificity than `button[data-...]`
  // alone.
  const linkButtonSpecificSelectors = [
    "a",
    'button[data-hierarchy="link"][data-theme]',
    'button[data-hierarchy="link_underline"][data-theme]',
  ];

  const baseSelectors = descendantPrefixes
    .flatMap((prefix) =>
      linkButtonSpecificSelectors.map((inner) => `${prefix}${inner}`),
    )
    .join(", ");
  const rules = [
    // Expose the link color as a CSS variable on the themed scope so
    // arbitrary class-styled links (e.g. .order-details-link on the orders
    // page, applied to clickable <Text> components that aren't anchors or
    // link-hierarchy buttons) can pick it up via fallback.
    `${scopes} { --redo-portal-link-color: ${color}; }`,
    `${baseSelectors} { color: ${color}; }`,
  ];

  // Auto-darken (or auto-lighten on dark themes) for hover. Skipped when the
  // input is unparseable so we don't emit a broken declaration.
  const hover = resolveHoverColor(color);
  if (hover !== undefined) {
    const hoverSelectors = descendantPrefixes
      .flatMap((prefix) =>
        linkButtonSpecificSelectors.map((inner) => `${prefix}${inner}:hover`),
      )
      .join(", ");
    rules.push(`${hoverSelectors} { color: ${hover}; }`);
  }

  // The card "back arrow" is a link-hierarchy RedoButton but should NOT
  // adopt the merchant's link color — it's a chrome control, not content.
  // Reset it to the neutral text-tertiary token so it tracks the dividers
  // rather than a vivid accent. `!important` because the link rule above
  // has the same specificity once the descendant selector kicks in.
  rules.push(
    `${s}[data-ui="back-button"] button, [data-ui="portal-modal"] [data-ui="back-button"] button { color: var(--redo-colors-text-text-tertiary) !important; }`,
  );

  return rules;
}
